import { Injectable } from '@angular/core';
import { MappingResource } from '@app/shared/resources/mapping.resource';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { NodePoint, UnmappedNode } from '@app/shared/models/unmapped-nodes-datasource';
import { SelectableNode, SensorNodeDTO } from '@app/shared/models/sensor-node';
import { IdsBatch } from '@app/shared/models/sensor-node-batch.interface';
import { VirtualNotification } from '@app/shared/models/virtual-notification-dto.interface';
import { DuplicateMappingResource } from '@app/shared/resources/duplicate-mapping.resource';
import { finalize } from 'rxjs/operators';

export interface NodeErrorMessage {
  nodeId: number;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class MappingService {
  private static readonly MAPPING_ID_COUNT = 32;
  private postMappingActivity: () => void;
  // various flags to indicate that mapping activity is in progress
  private activityInProgress = false;
  private activityWaiting = false;
  private mappingTried = false;
  private postActivityInProgress = false;
  constructor(
    private readonly mappingResource: MappingResource,
    private readonly duplicateMappingResource: DuplicateMappingResource
  ) {}

  private getAudio(mappingId: number): HTMLAudioElement {
    const audio = new Audio();
    audio.src = `/assets/static/mapping/${mappingId}.wav`;
    console.debug("Audio Source", audio.src);
    return audio;
  }

  private playAudio(mappingId: number, retries = 5, timeBetweenRetries = 100): Observable<void> {
    return new Observable<void>((observer) => {
      console.debug("PLaying audio")
      const attemptPlayback = (attempt: number) => {
        const audio = this.getAudio(mappingId);
        if (!audio) {
          observer.error(`Audio not loaded: ${mappingId}`);
          return;
        }
        audio.onended = () => {
          if (attempt < retries) {
            setTimeout(() => attemptPlayback(attempt + 1), timeBetweenRetries);
          } else {
            observer.complete();
          }
        };
        audio.oncanplaythrough = () => {
          audio.play().catch(() => observer.error(`Could not play audio: ${mappingId}`));
        };
        audio.load();
      };
      attemptPlayback(1);
    });
  }

  get isActivityInProgress(): boolean {
    return this.activityInProgress;
  }

  get isActivityWaiting(): boolean {
    return this.activityWaiting;
  }

  get wasMappingTried(): boolean {
    return this.mappingTried;
  }

  get isPostActivityInProgress(): boolean {
    return this.postActivityInProgress;
  }

  setMappingTried(value: boolean): void {
    this.mappingTried = value;
  }

  setPostActivityInProgress(val: boolean): void {
    this.postActivityInProgress = val;
  }

  setActivityWaiting(value: boolean): void {
    this.activityWaiting = value;
  }

  setPostMappingActivity(callback: () => void): void {
    this.postMappingActivity = callback;
  }

  clearMappingFlags(): void {
    this.activityInProgress = false;
    this.activityWaiting = false;
    this.mappingTried = false;
    this.postActivityInProgress = false;
  }

  doMapping(nodeId: number, buildingId: number): Observable<readonly unknown[]> {
    this.activityInProgress = true;
    return this.mappingResource.getMappingId(buildingId).pipe(
      switchMap(mappingId => {
        console.debug("mappingId", mappingId);
        return forkJoin([
          this.playAudio(mappingId),
          this.mappingResource.map(new VirtualNotification(nodeId, mappingId, buildingId))
        ]).pipe(
          finalize(() => {
            this.activityInProgress = false;
            this.postMappingActivity?.();
          })
        );
      })
    );
  }

  getUnmappedNodesList(buildingId: number): Observable<UnmappedNode[]> {
    return this.mappingResource.getUnmappedNodesList(buildingId);
  }

  mapGenericNode(target: NodePoint, floorId: number): Observable<SensorNodeDTO> {
    return this.mappingResource.mapGenericNode(target, floorId);
  }

  unmapGenericNode(target: SelectableNode): Observable<NodeErrorMessage[]> {
    return this.mappingResource.unmapGenericNode(target);
  }

  unmapGenericNodes(sensorNodeIdsBatch: IdsBatch): Observable<{}> {
    return this.mappingResource.unmap(sensorNodeIdsBatch);
  }
}

export default MappingService;
